import { ChangeDetectorRef, Component, OnDestroy, viewChild, inject } from '@angular/core';
import {
  BackendResponse,
  FileUploaderV2,
  FileV2Api,
  ModalRef,
  ReactiveComponent,
  WorkflowItem,
  deepClone,
  ModalModule,
  WorkflowModule,
  ButtonV2Module,
} from '@schenck/rotec-components';
import {
  MachineTaskService,
  ToolingTaskService,
  MachinesSearchService,
  ToolingSearchService,
} from 'src/services';
import {
  WORKFLOW_ITEMS_UPLOAD_MACHINE_TASKLIST,
  WORKFLOW_ITEMS_UPLOAD_TOOLING_TASKLIST,
} from 'src/utils/constants/upsert-task.const';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { TaskListStepComponent } from './steps/tasklist-step/tasklist-step.component';
import { NgSwitch, NgIf, NgSwitchCase, AsyncPipe } from '@angular/common';
import { MachinesStepComponent } from './steps/machines-step/machines-step.component';
import { ToolingStepComponent } from './steps/tooling-step/tooling-step.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'ma-upload-tasks-modal',
  templateUrl: './upload-tasks-modal.component.html',
  styleUrls: ['./upload-tasks-modal.component.less'],
  imports: [
    ModalModule,
    WorkflowModule,
    NgSwitch,
    NgIf,
    NgSwitchCase,
    MachinesStepComponent,
    ToolingStepComponent,
    TaskListStepComponent,
    ButtonV2Module,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class UploadTasksModalComponent extends ReactiveComponent implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalRef = inject<ModalRef<MachineTaskService | ToolingTaskService>>(ModalRef);
  private readonly fileApi = inject(FileV2Api);
  public machinesService = inject(MachinesSearchService);
  public toolingService = inject(ToolingSearchService);

  public readonly taskListComponent = viewChild<TaskListStepComponent>('taskList');
  public workflowItems = deepClone(
    this.isMachineTaskMode
      ? [...WORKFLOW_ITEMS_UPLOAD_MACHINE_TASKLIST]
      : [...WORKFLOW_ITEMS_UPLOAD_TOOLING_TASKLIST]
  );
  public activeItem: WorkflowItem = this.workflowItems[0];
  public lastStep: boolean = false;
  public firstStep: boolean = false;
  public nextClick$ = new Subject<void>();
  public previousClick$ = new Subject<void>();
  public pictureService = new FileUploaderV2(this.fileApi);
  public loading$ = new BehaviorSubject<boolean>(false);
  public file: File;

  public get isMachineTaskMode(): boolean {
    return this.modalRef.data instanceof MachineTaskService;
  }

  public get createButton(): boolean {
    return this.lastStep && this.activeItemIndex === this.workflowItems.length - 1;
  }

  public get activeItemIndex(): number {
    return this.workflowItems.findIndex(({ title }) => this.activeItem.title === title);
  }

  public get previousButtonDisabled(): boolean {
    return this.activeItemIndex === 0;
  }

  public get nextButtonDisabled(): boolean {
    if (this.lastStep) {
      return !this.file;
    } else {
      return this.isMachineTaskMode
        ? !this.machinesService?.state?.selectedIds.length
        : !this.toolingService?.state?.selectedIds.length;
    }
  }

  constructor() {
    super();
    this.machinesService.setSelected([]);
    this.toolingService.setSelected([]);
  }

  ngOnDestroy(): void {
    this.nextClick$.complete();
    this.previousClick$.complete();
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    super.ngAfterViewInit();
  }

  public itemChanged(item: WorkflowItem): void {
    this.activeItem = item;
    this.stepStateChanged();
  }

  public stepStateChanged(): void {
    this.lastStep = this.activeItemIndex === this.workflowItems.length - 1;
    this.firstStep = this.activeItemIndex === 0;
  }

  public nextStep(): void {
    this.nextClick$.next();
  }

  public previousStep(): void {
    this.previousClick$.next();
  }

  public close(): void {
    this.modalRef.close();
  }

  public fileUpload(file: File): void {
    this.file = file;
  }

  public save(): void {
    const assetIds = this.isMachineTaskMode
      ? this.machinesService.state.selectedIds
      : this.toolingService.state.selectedIds;

    assetIds.forEach((id) => {
      this.modalRef.data
        .createMultipleFromFile$(this.file, id)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          BackendResponse.reactToResponse<void>({
            success: () => this.modalRef.emitAndClose(true),
            badRequest: (msg) => this.taskListComponent().showErrorMsg(msg),
            notFound: () =>
              this.taskListComponent().showErrorMsg(
                this.isMachineTaskMode
                  ? 'VALIDATION_ERROR_TaskUpload Machine not found'
                  : 'VALIDATION_ERROR_TaskUpload Tooling not found'
              ),
            error: () =>
              this.taskListComponent().showErrorMsg(
                'TECHNICAL_ERROR_try again later or contact admin'
              ),
          })
        );
    });
  }
}
